import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import './LegalPage.css';

const LegalPage = () => {
  const { page } = useParams();
  const [activeTab, setActiveTab] = useState(page || 'disclaimer');
  
  useEffect(() => {
    if (page) {
      setActiveTab(page);
    }
    window.scrollTo(0, 0);
  }, [page]);

  const renderContent = () => {
    switch (activeTab) {
      case 'disclaimer':
        return (
          <div className="legal-content">
            <h2>Medical Disclaimer</h2>
            <p className="last-updated">Last Updated: March 13, 2025</p>
            
            <div className="legal-section">
              <h3>1. Informational Content Only</h3>
              <p>
                CataractLensAdvisor provides information about cataract lenses and related topics for educational 
                and informational purposes only. The content on this website is not intended to be a substitute 
                for professional medical advice, diagnosis, or treatment.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>2. Not Medical Advice</h3>
              <p>
                The information provided by CataractLensAdvisor, including lens recommendations, educational 
                resources, and other content, should not be considered medical advice. Our service is designed 
                to help you have more informed discussions with your healthcare providers, not to replace those 
                discussions.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>3. Consult Healthcare Providers</h3>
              <p>
                Always seek the advice of your ophthalmologist, physician, or other qualified healthcare provider 
                with any questions you may have regarding a medical condition or treatment options. Never disregard 
                professional medical advice or delay seeking it because of something you have read on this website.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>4. Recommendation Generation</h3>
              <p>
                Our lens recommendations are generated based on the information you provide in the questionnaire 
                and our database of available lens options. The recommendations are algorithmic in nature and 
                consider factors such as your stated vision needs, lifestyle, budget constraints, and medical 
                factors. However, they do not take into account your complete medical history, eye measurements, 
                or other factors that your doctor will consider.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>5. No Guarantee of Accuracy</h3>
              <p>
                While we strive to provide accurate and up-to-date information, we make no representations or 
                warranties of any kind, express or implied, about the completeness, accuracy, reliability, 
                suitability, or availability of the information, products, services, or related graphics 
                contained on this website.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>6. No Doctor-Patient Relationship</h3>
              <p>
                Use of CataractLensAdvisor does not create a doctor-patient relationship between you and 
                CataractLensAdvisor or any of its employees, contractors, or affiliates. The website does not 
                replace consultation with qualified healthcare professionals.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>7. Emergency Medical Situations</h3>
              <p>
                If you think you may have a medical emergency, call your doctor or emergency services immediately. 
                CataractLensAdvisor is not designed to address emergency medical needs.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>8. External Links</h3>
              <p>
                This website may contain links to external websites that are not provided or maintained by 
                CataractLensAdvisor. We do not guarantee the accuracy, relevance, timeliness, or completeness 
                of any information on these external websites.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>9. Use at Your Own Risk</h3>
              <p>
                Your use of CataractLensAdvisor is at your sole risk. The information is provided on an "as is" 
                basis. CataractLensAdvisor shall not be liable for any direct, indirect, incidental, consequential, 
                or punitive damages arising out of your access to or use of the website.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>10. Changes to Disclaimer</h3>
              <p>
                CataractLensAdvisor reserves the right to modify this disclaimer at any time. We encourage users 
                to frequently check this page for any changes. Your continued use of the website after any changes 
                are posted constitutes your acceptance of the modified disclaimer.
              </p>
            </div>
          </div>
        );
      
      case 'privacy':
        return (
          <div className="legal-content">
            <h2>Privacy Policy</h2>
            <p className="last-updated">Last Updated: March 13, 2025</p>
            
            <div className="legal-section">
              <h3>1. Introduction</h3>
              <p>
                CataractLensAdvisor ("we," "our," or "us") is committed to protecting your privacy. This Privacy 
                Policy explains how we collect, use, disclose, and safeguard your information when you visit our 
                website or use our services. Please read this Privacy Policy carefully. By continuing to use our 
                website, you consent to the practices described in this policy.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>2. Information We Collect</h3>
              <p>
                We may collect information about you in various ways, including:
              </p>
              <ul>
                <li>
                  <strong>Questionnaire Responses:</strong> Information you provide when completing our lens 
                  recommendation questionnaire, including vision needs, lifestyle factors, budget constraints, 
                  and medical factors.
                </li>
                <li>
                  <strong>Personal Information:</strong> Email address (if you choose to receive a premium report 
                  via email).
                </li>
                <li>
                  <strong>Payment Information:</strong> If you purchase a premium report, we collect payment 
                  information through our secure payment processor, Stripe. We do not store your full credit 
                  card details on our servers.
                </li>
                <li>
                  <strong>Usage Data:</strong> Information about how you use our website, including your browser 
                  type, IP address, pages visited, time spent on pages, and other usage statistics.
                </li>
                <li>
                  <strong>Cookies and Similar Technologies:</strong> We use cookies and similar tracking 
                  technologies to track activity on our website and hold certain information.
                </li>
              </ul>
            </div>
            
            <div className="legal-section">
              <h3>3. How We Use Your Information</h3>
              <p>
                We may use the information we collect for various purposes, including:
              </p>
              <ul>
                <li>To provide and maintain our service</li>
                <li>To generate personalized lens recommendations</li>
                <li>To process payments and deliver premium reports</li>
                <li>To improve our website and user experience</li>
                <li>To monitor usage of our website</li>
                <li>To detect, prevent, and address technical issues</li>
              </ul>
            </div>
            
            <div className="legal-section">
              <h3>4. Disclosure of Your Information</h3>
              <p>
                We may disclose your information in the following situations:
              </p>
              <ul>
                <li>
                  <strong>Service Providers:</strong> We may share your information with third-party vendors, 
                  service providers, and other third parties who perform services on our behalf (such as payment 
                  processing and email delivery).
                </li>
                <li>
                  <strong>Legal Requirements:</strong> We may disclose your information if required to do so by 
                  law or in response to valid requests by public authorities.
                </li>
                <li>
                  <strong>Business Transfers:</strong> We may share or transfer your information in connection 
                  with, or during negotiations of, any merger, sale of company assets, financing, or acquisition 
                  of all or a portion of our business.
                </li>
              </ul>
              <p>
                <strong>We do not sell your personal information to third parties.</strong>
              </p>
            </div>
            
            <div className="legal-section">
              <h3>5. Data Security</h3>
              <p>
                We implement appropriate technical and organizational measures to protect the security of your 
                personal information. However, please be aware that no method of transmission over the Internet 
                or method of electronic storage is 100% secure.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>6. Your Data Protection Rights</h3>
              <p>
                Depending on your location, you may have certain rights regarding your personal information, 
                including:
              </p>
              <ul>
                <li>The right to access the personal information we have about you</li>
                <li>The right to request correction of inaccurate personal information</li>
                <li>The right to request deletion of your personal information</li>
                <li>The right to object to processing of your personal information</li>
                <li>The right to data portability</li>
                <li>The right to withdraw consent</li>
              </ul>
              <p>
                To exercise any of these rights, please contact us using the information provided at the end 
                of this policy.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>7. Children's Privacy</h3>
              <p>
                Our website is not intended for children under 18 years of age. We do not knowingly collect 
                personal information from children under 18. If you are a parent or guardian and believe your 
                child has provided us with personal information, please contact us.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>8. Changes to This Privacy Policy</h3>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting 
                the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to 
                review this Privacy Policy periodically for any changes.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>9. Contact Us</h3>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <p>
                Email: privacy@cataractlensadvisor.com
              </p>
            </div>
          </div>
        );
      
      case 'terms':
        return (
          <div className="legal-content">
            <h2>Terms of Service</h2>
            <p className="last-updated">Last Updated: March 13, 2025</p>
            
            <div className="legal-section">
              <h3>1. Agreement to Terms</h3>
              <p>
                By accessing or using CataractLensAdvisor, you agree to be bound by these Terms of Service. If 
                you do not agree to these Terms, you should not access or use our website or services.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>2. Description of Service</h3>
              <p>
                CataractLensAdvisor provides an online platform that offers information about cataract lenses 
                and generates personalized lens recommendations based on user-provided information. We also 
                offer premium reports for a fee.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>3. User Accounts</h3>
              <p>
                CataractLensAdvisor does not currently require user accounts or registration to access our basic 
                services. However, if we implement user accounts in the future, you will be responsible for 
                maintaining the confidentiality of your account information and for all activities that occur 
                under your account.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>4. User Conduct</h3>
              <p>
                When using CataractLensAdvisor, you agree not to:
              </p>
              <ul>
                <li>Use the service for any illegal purpose or in violation of any local, state, national, or international law</li>
                <li>Violate or infringe other people's intellectual property, privacy, publicity, or other legal rights</li>
                <li>Impersonate any person or entity or misrepresent your affiliation with a person or entity</li>
                <li>Interfere with or disrupt the service or servers or networks connected to the service</li>
                <li>Attempt to gain unauthorized access to any portion of the service or any other systems or networks</li>
                <li>Use the service to transmit any viruses, worms, defects, Trojan horses, or other items of a destructive nature</li>
                <li>Use any robot, spider, site search/retrieval application, or other automated device, process or means to access, retrieve, scrape, or index any portion of the service</li>
              </ul>
            </div>
            
            <div className="legal-section">
              <h3>5. Intellectual Property</h3>
              <p>
                The content, organization, graphics, design, compilation, and other matters related to 
                CataractLensAdvisor are protected under applicable copyrights, trademarks, and other proprietary 
                rights. Copying, redistributing, using, or publishing any such proprietary content, except as 
                permitted by fair use or otherwise by these Terms of Service, is strictly prohibited without 
                prior written permission.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>6. Payment Terms</h3>
              <p>
                If you choose to purchase a premium report:
              </p>
              <ul>
                <li>You agree to pay the fees indicated for the service</li>
                <li>Payments are processed through our third-party payment processor, Stripe</li>
                <li>All payments are final and non-refundable, except as required by law</li>
                <li>We reserve the right to change our prices at any time</li>
              </ul>
            </div>
            
            <div className="legal-section">
              <h3>7. Disclaimer of Warranties</h3>
              <p>
                CataractLensAdvisor is provided on an "as is" and "as available" basis. We make no warranties, 
                expressed or implied, and hereby disclaim and negate all other warranties, including without 
                limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, 
                or non-infringement of intellectual property or other violation of rights.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>8. Limitation of Liability</h3>
              <p>
                In no event shall CataractLensAdvisor, its directors, employees, partners, agents, suppliers, 
                or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, 
                including without limitation, loss of profits, data, use, goodwill, or other intangible losses, 
                resulting from your access to or use of or inability to access or use the service.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>9. Indemnification</h3>
              <p>
                You agree to defend, indemnify, and hold harmless CataractLensAdvisor, its directors, employees, 
                partners, agents, suppliers, or affiliates, from and against any claims, liabilities, damages, 
                judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) 
                arising out of or relating to your violation of these Terms of Service or your use of the service.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>10. Governing Law</h3>
              <p>
                These Terms shall be governed and construed in accordance with the laws of the United States, 
                without regard to its conflict of law provisions.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>11. Changes to Terms</h3>
              <p>
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a 
                revision is material, we will try to provide at least 30 days' notice prior to any new terms 
                taking effect. What constitutes a material change will be determined at our sole discretion.
              </p>
            </div>
            
            <div className="legal-section">
              <h3>12. Contact Us</h3>
              <p>
                If you have any questions about these Terms, please contact us at:
              </p>
              <p>
                Email: terms@cataractlensadvisor.com
              </p>
            </div>
          </div>
        );
      
      default:
        return <div>Page not found</div>;
    }
  };

  return (
    <div className="legal-page">
      <div className="container">
        <div className="legal-container">
          <div className="legal-sidebar">
            <h3>Legal Documents</h3>
            <ul className="legal-nav">
              <li className={activeTab === 'disclaimer' ? 'active' : ''}>
                <Link to="/legal/disclaimer">Medical Disclaimer</Link>
              </li>
              <li className={activeTab === 'privacy' ? 'active' : ''}>
                <Link to="/legal/privacy">Privacy Policy</Link>
              </li>
              <li className={activeTab === 'terms' ? 'active' : ''}>
                <Link to="/legal/terms">Terms of Service</Link>
              </li>
            </ul>
          </div>
          
          <div className="legal-main">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
